/**
 * useNavigation Hook
 * Provides navigation utilities and breadcrumb generation
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useVertical } from '@/hooks/useVertical';
import { getNavigationForRole, getNavigationWithTerminology, NavigationItem } from '@/lib/navigation-config';
import { Role } from '@/types/vertical';

export interface Breadcrumb {
  label: string;
  href: string;
  current?: boolean;
}

interface UseNavigationReturn {
  getBreadcrumbs: () => Breadcrumb[];
  isActivePath: (path: string, options?: { exact?: boolean }) => boolean;
  navigationItems: NavigationItem[];
}

// Route config for breadcrumb labels
const ROUTE_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/agents': 'Agents',
  '/billing': 'Billing',
  '/skills': 'Skills',
  '/settings': 'Settings',
  '/valuation': 'Valuation',
};

// Dynamic route patterns
const DYNAMIC_PATTERNS: Array<{
  pattern: RegExp;
  label: string;
  parentPath: string;
}> = [
  { pattern: new RegExp('^/agents/[^/]+$'), label: 'Agent', parentPath: '/agents' },
];

export function useNavigation(): UseNavigationReturn {
  const location = useLocation();
  const { isPlatformAdmin } = useAuth();
  const { vertical, terminology } = useVertical();

  // Get navigation items based on platform admin status, vertical, and terminology
  const navigationItems = useMemo<NavigationItem[]>(() => {
    // NOTE: User.role is removed. Agency-scoped roles are on UserAgencyRole.
    // Default to MEMBER navigation for all users; platform admins get admin items added.
    let items = getNavigationForRole(Role.MEMBER);

    // Include admin items for platform admins
    if (isPlatformAdmin) {
      const adminItems = getNavigationForRole(Role.PLATFORM_ADMIN);
      // Add admin items that aren't already included
      const adminItemIds = new Set(items.map(i => i.id));
      adminItems.forEach(item => {
        if (!adminItemIds.has(item.id)) {
          items.push(item);
        }
      });
    }

    // Filter by vertical - keep items without vertical restriction OR items for this vertical
    items = items.filter(item => {
      return !item.vertical || item.vertical === vertical.id;
    });

    // Apply terminology translations
    items = getNavigationWithTerminology(items, terminology);

    return items;
  }, [isPlatformAdmin, vertical.id, terminology]);

  /**
   * Check if a given path is active
   */
  const isActivePath = (path: string, options: { exact?: boolean } = {}): boolean => {
    const { exact = false } = options;

    if (exact) {
      return location.pathname === path;
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  /**
   * Generate breadcrumbs for current route
   */
  const getBreadcrumbs = (): Breadcrumb[] => {
    const pathname = location.pathname;

    // Don't show breadcrumbs on home/dashboard
    if (pathname === '/' || pathname === '/dashboard') {
      return [];
    }

    const breadcrumbs: Breadcrumb[] = [];

    // Add dashboard as first breadcrumb
    breadcrumbs.push({
      label: 'Dashboard',
      href: '/dashboard',
    });

    // Check for dynamic routes
    for (const { pattern, label, parentPath } of DYNAMIC_PATTERNS) {
      const match = pathname.match(pattern);
      if (match) {
        // Add parent path
        if (parentPath) {
          const parentLabel = ROUTE_LABELS[parentPath] || label;
          breadcrumbs.push({
            label: parentLabel,
            href: parentPath,
          });
        }

        // Add current dynamic page
        breadcrumbs.push({
          label: `${label} Details`,
          href: pathname,
          current: true,
        });

        return breadcrumbs;
      }
    }

    // Handle static nested routes (e.g., /agents/settings)
    const pathSegments = pathname.split('/').filter(Boolean);
    let currentPath = '';

    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      currentPath += `/${segment}`;

      const isLast = i === pathSegments.length - 1;
      const routeLabel = ROUTE_LABELS[currentPath];

      if (routeLabel) {
        breadcrumbs.push({
          label: routeLabel,
          href: currentPath,
          current: isLast,
        });
      }
    }

    // If no breadcrumbs were added beyond dashboard, add current page
    if (breadcrumbs.length === 1) {
      const currentLabel = ROUTE_LABELS[pathname] || pathSegments[pathSegments.length - 1] || 'Page';
      breadcrumbs.push({
        label: currentLabel,
        href: pathname,
        current: true,
      });
    }

    return breadcrumbs;
  };

  return {
    getBreadcrumbs,
    isActivePath,
    navigationItems,
  };
}
